import { useCallback, useEffect, useRef, useState } from "react";
import type { AuthSession } from "./api";
import { requestJSON, type IDSOperation } from "./api";
import { DiagnosticError, DiagnosticText } from "./DiagnosticText";
import { useI18n, type Translator } from "./i18n";

type VolumeState = "mounted" | "pending" | "missing";

export type DataDiskNode = {
  node: string;
  instance_id: string | null;
  zone: string | null;
  mount_root: string | null;
  agent_version: string | null;
  status: "reporting" | "stale";
  disk_count: number;
  mounted_disks: number;
  pending_disks: number;
  missing_disks: number;
  assignable_disks: number;
  team_count: number;
  total_bytes: number | null;
  free_bytes: number | null;
  observed_at: string | null;
  last_report_at: string | null;
};

export type DataDisk = {
  disk_id: string;
  node: string | null;
  instance_id: string | null;
  zone: string | null;
  status: VolumeState;
  observed_state: string | null;
  observed_reason: string | null;
  mount_path: string | null;
  marker_disk_id: string | null;
  device: string | null;
  filesystem: string | null;
  volume_id: string | null;
  volume_source: string | null;
  volume_size_bytes: number | null;
  volume_state: string | null;
  total_bytes: number | null;
  free_bytes: number | null;
  used_bytes: number | null;
  used_ratio: number | null;
  team_count: number;
  teams: string[];
  assignable: boolean;
  note: string | null;
  configured_by: string | null;
  configured_at: string | null;
  first_seen_at: string | null;
  observed_at: string | null;
  last_report_at: string | null;
};

export type DataDiskSnapshot = {
  generated_at: string;
  stale_after_seconds: number;
  nodes: DataDiskNode[];
  disks: DataDisk[];
};

type PendingAction = "block" | "allow";

const path = "/api/v1/sandbox/data-disks";
const operationsPath = "/api/v1/sandbox/operations";

function formatBytes(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const units = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"];
  let scaled = Math.max(value, 0);
  let unit = 0;
  while (scaled >= 1024 && unit < units.length - 1) {
    scaled /= 1024;
    unit += 1;
  }
  return `${scaled >= 100 || unit === 0 ? Math.round(scaled) : scaled.toFixed(1)} ${units[unit]}`;
}

function locale(): string {
  // The page is server-rendered by the test harness; `document` may be absent.
  return typeof document === "undefined" ? "zh-CN" : document.documentElement.lang || "zh-CN";
}

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale(), {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date);
}

function formatLogTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale(), {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(date);
}

function statusLabel(status: VolumeState, t: Translator): string {
  return {
    mounted: t("已挂载", "Mounted"),
    pending: t("待挂载", "Pending"),
    missing: t("已失联", "Missing")
  }[status];
}

function statusTone(status: VolumeState): string {
  return status === "pending" ? "warning" : "failed";
}

function actionLabel(action: string, t: Translator): string {
  return {
    "node_storage.disk.block": t("禁止调度", "Blocked placement"),
    "node_storage.disk.allow": t("恢复调度", "Allowed placement"),
    "node_storage.disk.configure": t("配置数据盘", "Configured data disk")
  }[action] ?? action;
}

function isNodeStorageOperation(operation: IDSOperation): boolean {
  return operation.action.startsWith("node_storage.disk.");
}

function capacity(disk: DataDisk): { tone: "ok" | "warn" | "stop"; percent: number } | null {
  const ratio = disk.used_ratio;
  if (ratio === null || !Number.isFinite(ratio)) return null;
  return { tone: ratio >= 0.85 ? "stop" : ratio >= 0.8 ? "warn" : "ok", percent: Math.min(Math.max(ratio * 100, 0), 100) };
}

function CapacityCell({ disk }: { disk: DataDisk }) {
  const { t } = useI18n();
  const level = capacity(disk);
  if (!level) return <span className="read-only-label">{t("容量未知", "Capacity unknown")}</span>;
  return <div className="node-storage-capacity">
    <span className="capacity-track"><span className={`capacity-fill capacity-${level.tone}`} style={{ width: `${level.percent.toFixed(1)}%` }} /></span>
    <span className="capacity-text"><strong>{formatBytes(disk.used_bytes)}</strong> {t("已用", "used")} · {t("剩余", "free")} {formatBytes(disk.free_bytes)}</span>
  </div>;
}

export function NodeStorageConfiguration({
  disks,
  selection,
  pending,
  reason,
  busy,
  onToggle,
  onToggleAll,
  onAction,
  onCancelAction,
  onReasonChange,
  onApply
}: {
  disks: DataDisk[];
  selection: string[];
  pending: PendingAction | null;
  reason: string;
  busy: boolean;
  onToggle: (diskID: string, selected: boolean) => void;
  onToggleAll: (selected: boolean) => void;
  onAction: (action: PendingAction) => void;
  onCancelAction: () => void;
  onReasonChange: (value: string) => void;
  onApply: () => void;
}) {
  const { t } = useI18n();
  const selectable = disks.filter((disk) => disk.status === "mounted");
  const picked = selectable.filter((disk) => selection.includes(disk.disk_id));
  const allRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = picked.length > 0 && picked.length < selectable.length;
  }, [picked.length, selectable.length]);
  const selectionLabel = picked.length === 1 ? picked[0].disk_id : t(`${picked.length} 块盘`, `${picked.length} disks`);

  if (disks.length === 0) return <section className="panel">
    <div className="administrator-loading">{t("尚未发现数据盘。节点挂载云盘后，这里会自动出现对应盘位。", "No data disk discovered yet. Mount a volume on a node and its slot appears here automatically.")}</div>
  </section>;

  return <section className="panel">
    {pending
      ? <div className="node-storage-bar is-confirm">
        <span>{t("即将对", "About to")} <strong>{selectionLabel}</strong> {t(`执行「${pending === "block" ? "禁止调度" : "恢复调度"}」`, `run "${pending === "block" ? "Block placement" : "Allow placement"}"`)}</span>
        <input
          value={reason}
          maxLength={512}
          disabled={busy}
          placeholder={t("变更原因（必填，写入操作审计）", "Change reason (required; written to the operation audit)")}
          onChange={(event) => onReasonChange(event.target.value)}
        />
        <button className="primary-button" type="button" disabled={busy || !reason.trim() || picked.length === 0} onClick={onApply}>
          {busy ? t("执行中…", "Applying…") : t("确认执行", "Confirm")}
        </button>
        <button className="secondary-button" type="button" disabled={busy} onClick={onCancelAction}>{t("取消", "Cancel")}</button>
      </div>
      : picked.length > 0 ? <div className="node-storage-bar">
        <span>{t("已选", "Selected")} <strong>{picked.length}</strong> {t("块盘", "disks")}</span>
        <button className="secondary-button" type="button" onClick={() => onAction("block")}>{t("禁止调度到该盘", "Block placement")}</button>
        <button className="secondary-button" type="button" onClick={() => onAction("allow")}>{t("恢复调度", "Allow placement")}</button>
        <button className="text-button" type="button" onClick={() => onToggleAll(false)}>{t("取消选择", "Clear selection")}</button>
        <span className="node-storage-bar-hint">{t("禁止后新的 Team 工作区不会落到选中的盘；盘上已有工作区不受影响", "Blocked disks stop receiving new Team workspaces; existing workspaces stay in place")}</span>
      </div>
      : null}
    <div className="administrator-table-wrap">
      <table className="administrator-table">
        <thead><tr>
          <th className="node-storage-check">
            <input
              ref={allRef}
              type="checkbox"
              aria-label={t("全选可调度的数据盘", "Select all schedulable data disks")}
              disabled={selectable.length === 0}
              checked={selectable.length > 0 && picked.length === selectable.length}
              onChange={(event) => onToggleAll(event.target.checked)}
            />
          </th>
          <th>{t("数据盘", "Data disk")}</th>
          <th>{t("工作节点", "Worker node")}</th>
          <th>{t("云盘 ID", "Volume ID")}</th>
          <th>{t("已用 / 剩余", "Used / free")}</th>
          <th>Team {t("数量", "count")}</th>
          <th>{t("是否开启调度", "Scheduling")}</th>
        </tr></thead>
        <tbody>
          {disks.map((disk) => {
            const mounted = disk.status === "mounted";
            const selected = selection.includes(disk.disk_id);
            return <tr key={disk.disk_id} className={mounted ? "" : "node-storage-stale"}>
              <td className="node-storage-check">
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={!mounted || busy}
                  aria-label={t(`选择 ${disk.disk_id}`, `Select ${disk.disk_id}`)}
                  onChange={(event) => onToggle(disk.disk_id, event.target.checked)}
                />
              </td>
              <td><strong>{disk.disk_id}</strong>{mounted ? null : <span className={`user-state user-state-${statusTone(disk.status)}`}>{statusLabel(disk.status, t)}</span>}</td>
              <td>{disk.node ?? "—"}</td>
              <td title={disk.volume_id ?? undefined}>{disk.volume_id ?? "—"}</td>
              <td><CapacityCell disk={disk} /></td>
              <td title={disk.teams.join(", ")}>{disk.team_count}</td>
              <td>
                <button
                  type="button"
                  className={`legacy-model-toggle ${disk.assignable ? "is-on" : ""}`}
                  role="switch"
                  aria-checked={disk.assignable}
                  aria-label={t(`${disk.disk_id} 调度开关`, `${disk.disk_id} scheduling switch`)}
                  disabled={!mounted || busy}
                  title={mounted
                    ? disk.assignable ? t("可调度 — 点击禁止调度", "Schedulable — click to block") : t("已禁止调度 — 点击恢复调度", "Blocked — click to allow")
                    : t(`${statusLabel(disk.status, t)}：只有已挂载的数据盘可以调度`, `${statusLabel(disk.status, t)}: only mounted disks can be scheduled`)}
                  onClick={() => onAction(disk.assignable ? "block" : "allow")}
                ><span /></button>
              </td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>
  </section>;
}

export function NodeStorageOperationLog({ operations, loading, error }: { operations: IDSOperation[] | null; loading: boolean; error: unknown }) {
  const { t } = useI18n();
  return <section className="panel">
    {error ? <div className="global-error administrator-message" role="alert"><DiagnosticError error={error} /></div> : null}
    {loading && !operations ? <div className="administrator-loading" role="status">{t("正在读取操作日志…", "Loading the operation log…")}</div> : null}
    {operations && operations.length === 0 ? <div className="administrator-loading">{t("还没有数据盘调度变更记录。", "No data disk scheduling change recorded yet.")}</div> : null}
    {operations && operations.length > 0 ? <div className="administrator-table-wrap">
      <table className="administrator-table">
        <thead><tr>
          <th>{t("时间", "Time")}</th>
          <th>{t("操作者", "Actor")}</th>
          <th>{t("数据盘", "Data disk")}</th>
          <th>{t("变更", "Change")}</th>
          <th>{t("变更原因", "Reason")}</th>
          <th>{t("结果", "Result")}</th>
        </tr></thead>
        <tbody>
          {operations.map((operation) => <tr key={operation.operation_id}>
            <td>{formatLogTime(operation.created_at)}</td>
            <td>{operation.requested_by_admin_user_id}</td>
            <td><strong>{operation.target_id}</strong></td>
            <td>{actionLabel(operation.action, t)}</td>
            <td>{operation.reason}</td>
            <td>
              <span className={`user-state user-state-${operation.state === "succeeded" ? "active" : operation.state === "failed" ? "failed" : "pending"}`}>
                {operation.state === "succeeded" ? t("成功", "Succeeded") : operation.state === "failed" ? t("失败", "Failed") : t("处理中", "Pending")}
              </span>
              {operation.state === "failed" && operation.message ? <small>{<DiagnosticText value={operation} />}</small> : null}
            </td>
          </tr>)}
        </tbody>
      </table>
    </div> : null}
  </section>;
}

export function NodeStoragePage({ session }: { session: AuthSession }) {
  const { t } = useI18n();
  const [snapshot, setSnapshot] = useState<DataDiskSnapshot | null>(null);
  const [selection, setSelection] = useState<string[]>([]);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState("");
  const [applying, setApplying] = useState(false);
  const [section, setSection] = useState<"configure" | "operations">("configure");
  const [operations, setOperations] = useState<IDSOperation[] | null>(null);
  const [operationsLoading, setOperationsLoading] = useState(true);
  const [operationsError, setOperationsError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState("");
  const alive = useRef(true);
  const requestVersion = useRef(0);
  const operationsVersion = useRef(0);

  const refresh = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError(null);
    try {
      const value = await requestJSON<DataDiskSnapshot>(path);
      if (alive.current && version === requestVersion.current) {
        setSnapshot(value);
        setSelection((current) => {
          const mounted = new Set(value.disks.filter((disk) => disk.status === "mounted").map((disk) => disk.disk_id));
          return current.filter((diskID) => mounted.has(diskID));
        });
      }
    } catch (caught) {
      if (alive.current && version === requestVersion.current) setError(caught);
    } finally {
      if (alive.current && version === requestVersion.current) setLoading(false);
    }
  }, []);

  const loadOperations = useCallback(async () => {
    const version = ++operationsVersion.current;
    setOperationsLoading(true);
    setOperationsError(null);
    try {
      const value = await requestJSON<{ items: IDSOperation[] }>(operationsPath);
      if (alive.current && version === operationsVersion.current) setOperations(value.items.filter(isNodeStorageOperation));
    } catch (caught) {
      if (alive.current && version === operationsVersion.current) setOperationsError(caught);
    } finally {
      if (alive.current && version === operationsVersion.current) setOperationsLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void refresh();
    void loadOperations();
    return () => { alive.current = false; requestVersion.current++; operationsVersion.current++; };
  }, [refresh, loadOperations]);

  const disks = snapshot?.disks ?? [];
  const selectedDisks = disks.filter((disk) => selection.includes(disk.disk_id));

  function resetAction() {
    setPending(null);
    setReason("");
    setSelection([]);
  }

  async function apply() {
    if (!pending || !reason.trim() || applying) return;
    const assignable = pending === "allow";
    const targets = selectedDisks;
    if (targets.length === 0) return;
    setApplying(true);
    setError(null);
    setNotice("");
    const failures: Array<{ diskID: string; error: unknown }> = [];
    for (const disk of targets) {
      try {
        // The page no longer edits notes; it sends the observed value back so a
        // scheduling change never clears a note set through the API.
        const updated = await requestJSON<DataDisk>(`${path}/${encodeURIComponent(disk.disk_id)}`, {
          method: "PUT",
          csrfToken: session.csrf_token,
          reason: reason.trim(),
          body: { assignable, note: disk.note }
        });
        if (!alive.current) return;
        setSnapshot((current) => current ? { ...current, disks: current.disks.map((item) => item.disk_id === updated.disk_id ? updated : item) } : current);
      } catch (caught) {
        if (!alive.current) return;
        failures.push({ diskID: disk.disk_id, error: caught });
      }
    }
    if (alive.current) {
      setApplying(false);
      resetAction();
      if (failures.length > 0) {
        setError(new Error(t(
          `${targets.length - failures.length} / ${targets.length} 块盘已更新；${failures.map((item) => item.diskID).join("、")} 失败`,
          `${targets.length - failures.length} of ${targets.length} disks updated; ${failures.map((item) => item.diskID).join(", ")} failed`
        )));
      } else {
        setNotice(t(
          `已${assignable ? "恢复" : "禁止"} ${targets.length} 块盘的调度 · 已写入操作审计`,
          `${assignable ? "Allowed" : "Blocked"} scheduling on ${targets.length} disks; recorded in the operation audit`
        ));
      }
      await refresh();
      await loadOperations();
    }
  }

  const reportingNodes = snapshot ? snapshot.nodes.filter((node) => node.status === "reporting").length : 0;

  return <div className="page-stack node-storage">
    <header className="page-header">
      <div>
        <span className="eyebrow page-eyebrow">NODE STORAGE</span>
        <h1>{t("节点存储", "Node storage")}</h1>
        <p>{t("决定哪些数据盘允许承接新的 Team 工作区。数据盘由节点上的观察 DaemonSet 自动发现，人工只负责挂载云盘。", "Decide which data disks may host new Team workspaces. The observer DaemonSet on every node discovers the disks; humans only mount the volumes.")}</p>
      </div>
      <div className="page-header-actions">
        {snapshot ? <span className={`node-storage-observed ${reportingNodes === snapshot.nodes.length ? "" : "is-stale"}`}>
          {t(`${reportingNodes} / ${snapshot.nodes.length} 节点上报中`, `${reportingNodes} / ${snapshot.nodes.length} nodes reporting`)}
          <small>{t("观测于", "Observed")} {formatTimestamp(snapshot.generated_at)}</small>
        </span> : null}
        <button className="secondary-button" type="button" disabled={loading} onClick={() => { void refresh(); void loadOperations(); }}>
          {loading ? t("读取中…", "Loading…") : t("刷新", "Refresh")}
        </button>
      </div>
    </header>
    {error ? <div className="global-error administrator-message" role="alert"><DiagnosticError error={error} /></div> : null}
    {notice ? <div className="success-message" role="status">{notice}</div> : null}
    <div className="page-tabs" role="tablist" aria-label={t("页面内容分区", "Page sections")}>
      <button type="button" role="tab" aria-selected={section === "configure"} className={section === "configure" ? "page-tab page-tab-active" : "page-tab"} onClick={() => setSection("configure")}>
        <span>{t("节点配置", "Node configuration")}</span>
        {snapshot ? <small>{snapshot.disks.length}</small> : null}
      </button>
      <button type="button" role="tab" aria-selected={section === "operations"} className={section === "operations" ? "page-tab page-tab-active" : "page-tab"} onClick={() => setSection("operations")}>
        <span>{t("操作日志", "Operation log")}</span>
        {operations ? <small>{operations.length}</small> : null}
      </button>
    </div>
    {loading && !snapshot ? <div className="administrator-loading" role="status">{t("正在读取节点数据盘观测结果…", "Loading observed node storage…")}</div> : null}
    {section === "configure"
      ? snapshot ? <NodeStorageConfiguration
        disks={snapshot.disks}
        selection={selection}
        pending={pending}
        reason={reason}
        busy={applying}
        onToggle={(diskID, selected) => setSelection((current) => selected ? [...current, diskID] : current.filter((item) => item !== diskID))}
        onToggleAll={(selected) => setSelection(selected ? snapshot.disks.filter((disk) => disk.status === "mounted").map((disk) => disk.disk_id) : [])}
        onAction={(action) => { setPending(action); setNotice(""); }}
        onCancelAction={resetAction}
        onReasonChange={setReason}
        onApply={() => void apply()}
      /> : null
      : <NodeStorageOperationLog operations={operations} loading={operationsLoading} error={operationsError} />}
    {!loading && !snapshot && !error ? <div className="administrator-loading">{t("无法读取节点存储观测结果。", "Unable to read node storage observations.")}</div> : null}
  </div>;
}
